import { Handle, Node, NodeProps, Position } from '@xyflow/react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useContext, useState } from 'react';
import { PartnerPlatform } from '../../interfaces/PartnerPlatform/PartnerPlatform';
import { PartnerPlatformLogo } from '../../components/PartnerPlatform/PartnerPlatformLogo';
import { IntegrationStatusBadge } from '../../components/IntegrationStatus/IntegrationStatusBadge';
import { sharedColors } from '../../utils/Style';
import { PartnerPlatformSettingsDialog } from '../../components/Settings/PartnerPlatformSettingsDialog';
import { SynchronizationSummaryControllerContext } from './SynchronizationSummaryController';

export type CustomNodeData = {
  label: string;
  platform: PartnerPlatform;
  productCount?: number;
  status?: boolean;
  enablement?: boolean;
};

export type CustomNodeProps = NodeProps<Node<CustomNodeData>>;

export const CustomNode = ({ data }: CustomNodeProps) => {
  const { onRefreshEnablement, onRefreshStatus } = useContext(
    SynchronizationSummaryControllerContext,
  );

  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  return (
    <Box
      sx={{
        padding: '10px 20px',
        border: '1px solid #999',
        borderRadius: 8,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        }}
      >
        <PartnerPlatformSettingsDialog
          open={showSettingsDialog}
          onClose={() => setShowSettingsDialog(false)}
          partnerPlatform={data.platform}
          onSave={() => {
            setShowSettingsDialog(false);
            onRefreshEnablement();
            onRefreshStatus();
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: 220,
          gap: 0.5,
        }}
      >
        <PartnerPlatformLogo platform={data.platform} />

        <Typography sx={{ fontWeight: 600, fontSize: '18px', mr: 'auto' }}>
          {data.label}
        </Typography>

        <IntegrationStatusBadge
          status={
            data.status === undefined
              ? 'loading'
              : data.status
              ? 'success'
              : 'error'
          }
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {Number.isFinite(data.productCount) && (
          <Typography sx={{ fontSize: '12px' }}>
            {data.productCount} Ürün
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontSize: '12px' }}>Eşitleme Durumu:</Typography>

        {data.enablement === undefined ? (
          <CircularProgress size={16} />
        ) : (
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: data.enablement
                ? sharedColors.statusGreen
                : sharedColors.statusRed,
            }}
          >
            {data.enablement ? 'Açık' : 'Kapalı'}
          </Typography>
        )}

        {data.enablement !== undefined && (
          <IconButton
            color='primary'
            size='small'
            onClick={(ev) => {
              ev.stopPropagation();

              setShowSettingsDialog(true);
            }}
          >
            <SettingsIcon fontSize='small' />
          </IconButton>
        )}
      </Box>

      <Handle type='source' position={Position.Right} style={{ opacity: 0 }} />
      <Handle type='target' position={Position.Left} style={{ opacity: 0 }} />
    </Box>
  );
};

const pulseStyle = {
  position: 'absolute' as const,
  top: '-20px',
  left: '-20px',
  right: '-20px',
  bottom: '-20px',
  borderRadius: '50%',
  background: 'rgba(30, 136, 229, 0.4)',
  filter: 'blur(25px)',
  animation: 'pulseGlow 2.5s infinite ease-in-out',
  zIndex: -1,
};

const styles = `
@keyframes pulseGlow {
  0%   { opacity: 0.4; transform: scale(0.85); }
  50%  { opacity: 0.9; transform: scale(1.05); }
  100% { opacity: 0.4; transform: scale(0.85); }
}
`;

export const CentralNode = ({ data }: CustomNodeProps) => {
  return (
    <Box
      sx={{
        position: 'relative',
        padding: '10px 20px',
        border: '1px solid #999',
        borderRadius: 8,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
    >
      <Box sx={pulseStyle} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {data.label}
        </Typography>
      </Box>
      {Number.isFinite(data.productCount) && (
        <Typography sx={{ fontSize: '12px' }}>
          {data.productCount} Ürün
        </Typography>
      )}

      <Handle type='source' position={Position.Right} style={{ opacity: 0 }} />
      <Handle type='target' position={Position.Left} style={{ opacity: 0 }} />

      <style>{styles}</style>
    </Box>
  );
};
